import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    Card,
    Divider,
    Chip,
    Stack,
    TextField,
    Collapse,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";

import { useLocation, useNavigate } from "react-router-dom";

import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

import CartToast from "../components/CartToast";

const FeaturedProductDetails = () => {
    const { state } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        document.title = "Featured Product Details | Tecniqa";
    }, []);

    const images = state?.image ? [state.image] : [];

    const [selectedImage, setSelectedImage] = useState("");

    useEffect(() => {
        if (state?.image) {
            setSelectedImage(state.image);
        }
    }, [state?.image]);

    const [toast, setToast] = useState({
        visible: false,
        message: "",
        type: "success",
    });

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2800);
    };

    const [enquiryOpen, setEnquiryOpen] = useState(false);
    const [enquirySent, setEnquirySent] = useState(false);
    const [enquiryData, setEnquiryData] = useState({
        name: "",
        email: "",
        phone: "",
        quantity: "",
        message: "",
    });

    if (!state) {
        return (
            <Typography sx={{ textAlign: "center", mt: 6 }}>
                Product not found
            </Typography>
        );
    }

    const handleEnquirySubmit = () => {
        if (!enquiryData.name || !enquiryData.email || !enquiryData.phone) {
            showToast("Please fill in all required fields.", "warning");
            return;
        }
        console.log("Enquiry:", { product: state.title, ...enquiryData });
        setEnquirySent(true);
        showToast("Enquiry submitted successfully!", "success");
        setTimeout(() => {
            setEnquiryOpen(false);
            setEnquirySent(false);
            setEnquiryData({ name: "", email: "", phone: "", quantity: "", message: "" });
        }, 2000);
    };

    return (
        <Box
            sx={{
                background: "linear-gradient(to bottom, #f5f7f2, #eef2ec)",
                minHeight: "100vh",
                py: 3,
                px: 2,
            }}
        >
            <Box sx={{ maxWidth: 1180, mx: "auto" }}>

                {/* BACK BUTTON */}
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        textTransform: "none",
                        borderRadius: "12px",
                        fontWeight: 600,
                        mb: 2.5,
                        color: "#1d2939",
                        borderColor: "#d0d5dd",
                        px: 2.2,
                        py: 0.8,
                        fontSize: "13px",
                        backgroundColor: "#fff",
                        "&:hover": { borderColor: "#98a2b3", backgroundColor: "#f8fafc" },
                    }}
                >
                    Back
                </Button>

                {/* MAIN CARD */}
                <Card
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: "28px",
                        background: "rgba(255,255,255,0.92)",
                        border: "1px solid rgba(226,232,240,0.9)",
                        boxShadow: "0 10px 35px rgba(15,23,42,0.06)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 4,
                            alignItems: "flex-start",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        {/* LEFT — Images */}
                        <Box sx={{ width: { xs: "100%", md: "42%" }, display: "flex", gap: 1.5 }}>
                            {/* Thumbnails */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {images.map((img, index) => (
                                    <Box
                                        key={index}
                                        onMouseEnter={() => setSelectedImage(img)}
                                        sx={{
                                            width: 62,
                                            height: 62,
                                            border: selectedImage === img
                                                ? "2px solid #111827"
                                                : "1px solid #d0d5dd",
                                            borderRadius: "14px",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            backgroundColor: "#fff",
                                            p: 0.5,
                                            transition: "0.3s ease",
                                            "&:hover": { borderColor: "#667085", transform: "translateY(-2px)" },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={img}
                                            alt=""
                                            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                                        />
                                    </Box>
                                ))}
                            </Box>

                            {/* Main image */}
                            <Box
                                sx={{
                                    flex: 1,
                                    border: "1px solid #e4e7ec",
                                    borderRadius: "24px",
                                    background: "linear-gradient(145deg,#ffffff,#f8fafc)",
                                    p: 2,
                                    minHeight: 390,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    overflow: "hidden",
                                }}
                            >
                                <InnerImageZoom
                                    src={selectedImage}
                                    zoomSrc={selectedImage}
                                    zoomType="hover"
                                    zoomScale={1.4}
                                    hideHint={true}
                                    width={360}
                                    height={360}
                                    imgAttributes={{
                                        style: { objectFit: "contain", width: "100%", height: "100%" },
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* RIGHT — Info */}
                        <Box sx={{ flex: 1 }}>
                            {/* Title */}
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    color: "#111827",
                                    lineHeight: 1.25,
                                    fontSize: { xs: "28px", md: "36px" },
                                    wordBreak: "break-word",
                                }}
                            >
                                {state.title}
                            </Typography>

                            {/* Brand + Stock + Featured chip */}
                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ mt: 1.5, flexWrap: "wrap" }}
                            >
                                <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                    Brand :
                                    <Box component="span" sx={{ color: "#111827", fontWeight: 700, ml: 0.7 }}>
                                        {state.brand}
                                    </Box>
                                </Typography>
                                <Chip
                                    label={state.stock}
                                    size="small"
                                    sx={{ backgroundColor: "#ecfdf3", color: "#027a48", fontWeight: 700, borderRadius: "8px" }}
                                />
                                <Chip
                                    label="Featured"
                                    size="small"
                                    sx={{ backgroundColor: "#eff6ff", color: "#1d4ed8", fontWeight: 700, borderRadius: "8px" }}
                                />
                            </Stack>

                            {/* Price — As per price tag */}
                            <Box
                                sx={{
                                    mt: 2.5,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                    border: "1px dashed #cbd5e1",
                                    borderRadius: "14px",
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography fontWeight={700} sx={{ color: "#475467", fontSize: "15px", letterSpacing: "0.3px" }}>
                                    Price:
                                </Typography>
                                <Typography fontWeight={800} sx={{ color: "#111827", fontSize: "17px", fontStyle: "italic" }}>
                                    As per price
                                </Typography>
                            </Box>

                            {/* Description */}
                            <Typography
                                sx={{ mt: 2.5, color: "#667085", lineHeight: 1.9, fontSize: "14px" }}
                            >
                                High-quality featured product designed for industrial performance, durability and efficiency in HVACR applications.
                            </Typography>

                            {/* Highlights */}
                            <Box
                                sx={{
                                    mt: 3,
                                    background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "18px",
                                    p: 2,
                                }}
                            >
                                <Typography fontWeight={700} sx={{ color: "#111827", mb: 1.5, fontSize: "16px" }}>
                                    Highlights
                                </Typography>
                                <Grid container spacing={1}>
                                    {[
                                        "Industrial grade",
                                        "Energy efficient",
                                        "Premium quality",
                                        "Reliable performance",
                                    ].map((item, index) => (
                                        <Grid size={6} key={index}>
                                            <Typography sx={{ fontSize: "13px", color: "#475467" }}>
                                                • {item}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>

                            {/* Action Buttons */}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 4 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<WhatsAppIcon />}
                                    href={`https://wa.me/919583688581?text=Hi, I'm interested in ${state.title}`}
                                    target="_blank"
                                    sx={{
                                        background: "linear-gradient(135deg,#25d366,#128c7e)",
                                        py: 1.2, px: 3, borderRadius: "14px",
                                        textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#fff",
                                        "&:hover": { background: "linear-gradient(135deg,#20c45e,#0f7a6e)" },
                                    }}
                                >
                                    Order on WhatsApp
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={<HelpOutlineOutlinedIcon />}
                                    onClick={() => setEnquiryOpen((v) => !v)}
                                    sx={{
                                        background: enquiryOpen
                                            ? "linear-gradient(135deg,#c89c22,#e9c454)"
                                            : "linear-gradient(135deg,#d4af37,#f5d76e)",
                                        py: 1.2, px: 3, borderRadius: "14px",
                                        textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#111827",
                                        boxShadow: "0 10px 25px rgba(212,175,55,0.28)",
                                        "&:hover": { background: "linear-gradient(135deg,#c89c22,#e9c454)" },
                                    }}
                                >
                                    {enquiryOpen ? "Close Enquiry" : "Enquiry About Product"}
                                </Button>
                            </Stack>

                            {/* Enquiry Form */}
                            <Collapse in={enquiryOpen} timeout={350}>
                                <Box
                                    sx={{
                                        mt: 2.5,
                                        background: "linear-gradient(135deg,#fffbeb,#fefce8)",
                                        border: "1px solid #fde68a",
                                        borderRadius: "20px",
                                        p: 2.5,
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                        <Typography fontWeight={700} sx={{ color: "#111827", fontSize: "15px" }}>
                                            Product Enquiry
                                        </Typography>
                                        <Box
                                            onClick={() => setEnquiryOpen(false)}
                                            sx={{
                                                cursor: "pointer", color: "#667085",
                                                display: "flex", alignItems: "center",
                                                "&:hover": { color: "#111827" },
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </Box>
                                    </Stack>

                                    {enquirySent ? (
                                        <Typography sx={{ textAlign: "center", color: "#027a48", fontWeight: 700, py: 2, fontSize: "15px" }}>
                                            ✅ Enquiry submitted! We'll get back to you soon.
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {[
                                                { label: "Full Name *", key: "name", type: "text", sm: 6 },
                                                { label: "Email Address *", key: "email", type: "email", sm: 6 },
                                                { label: "Phone Number *", key: "phone", type: "text", sm: 6 },
                                                { label: "Quantity Required", key: "quantity", type: "number", sm: 6 },
                                            ].map(({ label, key, type, sm }) => (
                                                <Grid size={{ xs: 12, sm: sm }} key={key}>
                                                    <TextField
                                                        label={label}
                                                        size="small"
                                                        fullWidth
                                                        type={type}
                                                        value={enquiryData[key]}
                                                        onChange={(e) =>
                                                            setEnquiryData((d) => ({ ...d, [key]: e.target.value }))
                                                        }
                                                        sx={inputSx}
                                                    />
                                                </Grid>
                                            ))}
                                            <Grid size={12}>
                                                <TextField
                                                    label="Message / Requirements"
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    value={enquiryData.message}
                                                    onChange={(e) =>
                                                        setEnquiryData((d) => ({ ...d, message: e.target.value }))
                                                    }
                                                    sx={inputSx}
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <Button
                                                    variant="contained"
                                                    endIcon={<SendIcon />}
                                                    fullWidth
                                                    onClick={handleEnquirySubmit}
                                                    sx={{
                                                        background: "linear-gradient(135deg,#111827,#1f2937)",
                                                        py: 1.1, borderRadius: "12px",
                                                        textTransform: "none", fontWeight: 700, fontSize: "14px",
                                                        boxShadow: "0 8px 20px rgba(17,24,39,0.2)",
                                                        "&:hover": { background: "linear-gradient(135deg,#0f172a,#111827)" },
                                                    }}
                                                >
                                                    Submit Enquiry
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>

                    {/* LOWER SECTION */}
                    <Divider sx={{ my: 4, borderColor: "#eaecf0" }} />

                    {/* Description — full width */}
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: "#111827", mb: 1.5 }}>
                            Product Description
                        </Typography>
                        <Typography sx={{ color: "#667085", lineHeight: 1.9, fontSize: "14px" }}>
                            {state.title} is engineered for professional HVACR and industrial applications with exceptional durability and premium-grade efficiency.
                        </Typography>
                    </Box>

                    {/* Specifications — 4 cards in single row */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: "#111827", mb: 1.5 }}>
                            Specifications
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "nowrap" }}>
                            {[
                                { label: "Brand", value: state.brand },
                                { label: "Category", value: "Featured Product" },
                                { label: "Availability", value: state.stock },
                                { label: "Warranty", value: "1 Year" },
                            ].map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: "1 1 0",
                                        background: "#f8fafc",
                                        border: "1px solid #e4e7ec",
                                        borderRadius: "14px",
                                        p: 1.5,
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "13px", mb: 0.5 }}>
                                        {item.label}
                                    </Typography>
                                    <Typography sx={{ color: "#667085", fontSize: "13px" }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Card>
            </Box>

            <CartToast visible={toast.visible} message={toast.message} type={toast.type} />
        </Box>
    );
};

export default FeaturedProductDetails;

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px", backgroundColor: "#fff", fontSize: "13px",
        "& fieldset": { borderColor: "#e4e7ec" },
        "&:hover fieldset": { borderColor: "#667085" },
        "&.Mui-focused fieldset": { borderColor: "#111827" },
    },
    "& .MuiInputLabel-root": { fontSize: "13px", color: "#667085" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#111827" },
};